import type { FC, MouseEvent } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@remix-run/react';
import cx from 'classnames';

import PushPinIcon from '~/components/Icons/PushPinIcon';
import { COMMUNITY_ROUTES } from '~/constants/community';
import { ImpressionSuppressionContext } from '~/context/ImpressionSuppressionContext';
import { useNectarImpression } from '~/hooks/useNectarImpression';
import type { CommunityPost } from '~/interfaces/community';
import { CommunitySendableType } from '~/interfaces/community/community';
import type { NectarPageBlock } from '~/utils/nectarPageBlock';

import { IconButton } from '../../../ui/IconButton';

import { useCommunityPostOptimistic } from '../../../../_hooks/useCommunityPostOptimistic';
import { useSpace } from '../../../../_hooks/useSpace';
import { PinnedPostContent } from './PinnedPostContent';

interface PinnedPostsProps {
  pinnedPosts: CommunityPost[];
  pageBlock?: NectarPageBlock;
}

interface PinnedPostImpressionProps {
  post: CommunityPost;
  hideSpace: boolean;
  pageBlock?: NectarPageBlock;
}

const PinnedBadge: FC = () => {
  const { t } = useTranslation();

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-bold uppercase tracking-wide leading-4 text-white"
      style={{ background: 'var(--community-primary-on-light)' }}
    >
      <PushPinIcon className="size-3" />
      {t('community.pinned_spotlight.chip')}
    </span>
  );
};

// Must be mounted with key={post.id} at the call site: a fresh mount per pin is
// what lets useNectarImpression's firedRef reset, so paging counts a new impression.
const PinnedPostImpression: FC<PinnedPostImpressionProps> = ({ post, hideSpace, pageBlock }) => {
  const suppressed = useContext(ImpressionSuppressionContext);
  const impressionRef = useNectarImpression(
    {
      sendableId: post.id,
      sendableType: CommunitySendableType.CommunityPost,
      pageBlock,
      communitySpaceId: post.space?.id,
    },
    { fireWhenInViewport: true, enabled: !!pageBlock && !suppressed },
  );

  return (
    <section ref={impressionRef}>
      <PinnedPostContent post={post} hideSpace={hideSpace} />
    </section>
  );
};

export const PinnedPosts: FC<PinnedPostsProps> = ({ pinnedPosts, pageBlock }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const space = useSpace();
  const { mergePost } = useCommunityPostOptimistic();
  const [index, setIndex] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset pager on channel change, not on every pinnedPosts update
  useEffect(() => {
    setIndex(0);
  }, [space?.slug]);

  const total = pinnedPosts.length;
  const safeIndex = Math.min(index, Math.max(0, total - 1));
  const current = pinnedPosts[safeIndex];

  if (!current) return null;

  const merged = mergePost(current);

  const handleCardClick = (event: MouseEvent<HTMLElement>) => {
    if (event.target instanceof Element && event.target.closest('a, button, [role="menu"]')) return;
    navigate(COMMUNITY_ROUTES.post(current.id));
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard access goes through the title link and pager buttons inside
    <article
      data-testid="pinned-spotlight"
      onClick={handleCardClick}
      className="w-full cursor-pointer rounded-3xl border p-4"
      style={{
        borderColor: 'var(--community-primary-on-light)',
        background: 'color-mix(in srgb, var(--community-primary) 5%, transparent)',
      }}
    >
      <div className="flex items-center gap-2">
        <PinnedBadge />
        <span aria-live="polite" className="sr-only">
          {t('community.pinned_spotlight.pin_position', { current: safeIndex + 1, total })}
        </span>
        {total > 1 && (
          // biome-ignore lint/a11y/useSemanticElements: role="group" is the standard ARIA pattern for a button toolbar
          <div
            role="group"
            aria-label={t('community.pinned_spotlight.pager_label')}
            className="ml-auto flex items-center gap-2"
          >
            <span className="text-[13px] text-black/60">{`${safeIndex + 1} / ${total}`}</span>
            <IconButton
              aria-label={t('community.pinned_spotlight.previous_pin')}
              className={cx('size-6 max-md:size-11', { 'opacity-30': safeIndex === 0 })}
              disabled={safeIndex === 0}
              onClick={() => setIndex(safeIndex - 1)}
            >
              <ChevronLeftIcon className="size-4" />
            </IconButton>
            <IconButton
              aria-label={t('community.pinned_spotlight.next_pin')}
              className={cx('size-6 max-md:size-11', { 'opacity-30': safeIndex === total - 1 })}
              disabled={safeIndex === total - 1}
              onClick={() => setIndex(safeIndex + 1)}
            >
              <ChevronRightIcon className="size-4" />
            </IconButton>
          </div>
        )}
      </div>

      <div className="mt-3">
        <PinnedPostImpression key={merged.id} post={merged} hideSpace={!!space} pageBlock={pageBlock} />
      </div>
    </article>
  );
};
